import { useOidc, useOidcAccessToken } from '@axa-fr/react-oidc';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import AccessEndedScreen from '../components/AccessEndedScreen';
import AccountMismatchScreen from '../components/AccountMismatchScreen';
import LoginScreen, {
  EmailSignIn,
  type LoginReason,
  type PlatformExit,
  RedirectingToSignIn,
} from '../components/LoginScreen';
import type { AuthProviderConfig } from '../types/api';
import { getActiveProvider } from './config';
import { readAccount, rememberAccount } from './lastAccount';
import { toRoutePath } from './navigation';
import {
  authorizeExtras,
  countScopes,
  EXPECTED_SCOPE_COUNT,
  emailFromPayload,
  narrowsScopesAfterLogin,
} from './oidc';
import { attemptSilentSignIn, clearSilentSignInAttempt, signInAgainAfterRefusal } from './reauth';
import { forgetRequestedAccount, readRequestedAccount, sameAccount } from './requestedAccount';
import {
  setAccessEndedHandler,
  setSessionRecoverer,
  setSignInAgain,
  setUnauthorizedHandler,
} from './session';
import {
  goToProviderScreen,
  isSignInPath,
  providerNamedByPath,
  signInRootPath,
} from './signInRoute';
import {
  openPlatformSignOut,
  setPlatformSignOutHandler,
  setSignedOutHandler,
  signOutEverywhere,
} from './signOut';
import { setAccessToken } from './token';

/**
 * Whether the provider has just redirected back here with a refusal.
 *
 * Read the same way `LoginScreen` reads it, since it is the same query parameter and the
 * same event: `error_description` when there is one, `error` otherwise.
 */
function hasProviderError(): boolean {
  const params = new URLSearchParams(window.location.search);
  return !!(params.get('error_description') || params.get('error'));
}

/**
 * Renders the app for a signed-in user, and the sign-in page for everyone else.
 *
 * It also owns the three things that are easy to leave out and invisible when missing: the
 * token the API layer attaches, the forced refresh that narrows a session's scopes, and
 * the record of who signed in, which is what lets the sign-in page say whose session the
 * browser is about to reuse.
 */
export default function AuthGate({
  children,
  providers,
  onUseDifferentProvider = null,
  autoSignIn = false,
  silentResume = true,
  loginHint = null,
  initiallySignedOut = false,
}: {
  children: ReactNode;
  /**
   * Every provider this deployment offers, needed only so a sign-out can render the
   * front door rather than the screen for the provider that was just left.
   */
  providers: AuthProviderConfig[];
  onUseDifferentProvider?: (() => void) | null;
  /**
   * Whether the person has already asked to sign in, so this gate should redirect rather
   * than render a button.
   *
   * True when a provider was just resolved from a typed address or named on the URL. It
   * arrives as a prop rather than being read from storage on purpose: it describes one
   * action in this page's lifetime, so a reload loses it and the screen appears instead
   * of a redirect firing at somebody who never asked for one.
   */
  autoSignIn?: boolean;
  /**
   * Whether the one silent sign-in may run. Off on the provider screens: somebody there
   * has pressed Back to get away from the identity provider, or followed a link naming a
   * provider, and sending them straight on would be the loop this exists to prevent. On
   * for the app paths and the email step, where a remembered account means a returning
   * person rather than a new one.
   */
  silentResume?: boolean;
  /**
   * The address typed on the email step, if this page load followed from it. Handed to
   * the identity provider as a hint where that saves a screen, and compared with whatever
   * it hands back, since a hint is a request and not an instruction: the same address is
   * stored by `goToProviderScreen`, because this prop does not survive the round trip.
   */
  loginHint?: string | null;
  /**
   * Whether this page load is the return from an in-place sign-out, so the screen should
   * say so. Only a single-provider deployment reaches the gate in that state; with two
   * providers the return lands on the email step, which takes the same flag directly.
   */
  initiallySignedOut?: boolean;
}) {
  // Fixed before this component mounted, so it never changes under a render.
  const provider = getActiveProvider();
  const { isAuthenticated, login, renewTokens } = useOidc();
  const navigate = useNavigate();
  // A sign-in started from one of our sign-in paths must not come back to it: the callback
  // returns to where the login began, and `/signin/<provider>` is not a place to land a
  // signed-in person. So it is sent to the app root instead. Started anywhere else, the
  // library's default of "back to where you were" is right.
  const onSignInPath = isSignInPath(window.location.pathname);
  const startSignIn = useCallback(
    () =>
      login(
        onSignInPath ? import.meta.env.BASE_URL : undefined,
        // The remembered account decides whether Entra is asked to show its account
        // picker, so it is read as the login starts rather than snapshotted at render:
        // a sign-out clears it, and the value that matters is the one at this moment.
        provider ? authorizeExtras(provider, loginHint, readAccount()) : undefined,
      ),
    [login, onSignInPath, provider, loginHint],
  );
  const { accessToken, accessTokenPayload } = useOidcAccessToken();
  const [sessionLost, setSessionLost] = useState(false);
  // A 403 with the `access_ended` code: a known account that is no longer admitted. Not a
  // session problem, so it must not become a re-login prompt; see components/AccessEndedScreen.
  const [accessEnded, setAccessEnded] = useState(false);
  const [signedOut, setSignedOut] = useState(initiallySignedOut);
  const [platformExit, setPlatformExit] = useState<PlatformExit>('none');
  const [resumeDeclined, setResumeDeclined] = useState(false);
  // The address typed at the email step, if this browser typed one. Read once per page
  // load with a lazy initialiser: it is written just before the redirect to the identity
  // provider, and this is the page that comes back with the answer.
  const [requestedAccount] = useState(readRequestedAccount);
  const [mismatchAccepted, setMismatchAccepted] = useState(false);
  const narrowingRequested = useRef(false);

  // Published during render rather than from an effect, because child effects run before
  // the parent's: a component that fetches on mount would otherwise send its first
  // request before the token was set and be answered 401 for no reason. The write is
  // idempotent, which is what makes it safe here.
  setAccessToken(accessToken ?? null);

  useEffect(() => {
    setUnauthorizedHandler(() => setSessionLost(true));
    setAccessEndedHandler(() => setAccessEnded(true));
    setSignedOutHandler(() => setSignedOut(true));
    // The menu's sign-out reports through here, and so does the "Reopen that tab" link
    // on the notice it leaves behind. It is the same three-step story either way, so it
    // is told in one place.
    setPlatformSignOutHandler(setPlatformExit);
    return () => {
      setUnauthorizedHandler(null);
      setAccessEndedHandler(null);
      setSignedOutHandler(null);
      setPlatformSignOutHandler(null);
    };
  }, []);

  // A 401 renews before it becomes `sessionLost`; see `recoverSession`. A renewal that
  // fails also reaches `onSessionLost` in `main.tsx`, which spends the one silent sign-in,
  // so it is not attempted a second time here.
  useEffect(() => {
    setSessionRecoverer(async () => {
      const tokens = await renewTokens();
      return 'accessToken' in tokens && !!tokens.accessToken;
    });
    return () => setSessionRecoverer(null);
  }, [renewTokens]);

  // A renewal that could not save a refused request; see `signInAgainAfterRefusal`.
  useEffect(() => {
    setSignInAgain(() => signInAgainAfterRefusal());
    return () => setSignInAgain(null);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      setSessionLost(false);
      setSignedOut(false);
      setPlatformExit('none');
      // A session is running, so the one automatic recovery is available again. Without
      // this the guard stays spent for the lifetime of the tab, and the second expiry of
      // a long day lands on the sign-in screen even though the first recovered silently.
      clearSilentSignInAttempt();
    } else {
      narrowingRequested.current = false;
      // The refused session is gone, so the terminal screen goes with it: what follows is
      // the ordinary sign-in, where a different account can be used.
      setAccessEnded(false);
    }
  }, [isAuthenticated]);

  // Recorded on every render of a signed-in session rather than once at login, because the
  // token is replaced by the narrowing refresh below and a session can start from a
  // restored worker with no login at all. Writing the same address twice costs nothing.
  useEffect(() => {
    if (isAuthenticated) {
      rememberAccount(emailFromPayload(accessTokenPayload));
    }
  }, [isAuthenticated, accessTokenPayload]);

  const signedInAs = emailFromPayload(accessTokenPayload);

  // The typed address has done its job the moment the session agrees with it, and holding
  // on to it would put the screen below in front of the next sign-in of this tab. A
  // mismatch keeps it instead, so a reload says the same thing rather than quietly letting
  // the wrong account through on the second look.
  useEffect(() => {
    if (isAuthenticated && sameAccount(requestedAccount, signedInAs)) forgetRequestedAccount();
  }, [isAuthenticated, requestedAccount, signedInAs]);

  // The authorization-code exchange cannot be asked for a narrow token: it rejects a
  // `scope` parameter outright, and the authorize request's scope does not constrain the
  // access token either, so the first token of every session carries all 38 platform
  // scopes. A refresh does honour scope. So the moment a session starts over-scoped, we
  // replace it, and what the app attaches from then on is a pure identity assertion.
  useEffect(() => {
    // Only MIKE Cloud hands back more than was asked for. Guarding on the provider
    // rather than on the scope count, because Entra's one scope happens to sit below the
    // MIKE Cloud threshold and relying on that would be relying on a coincidence.
    if (!isAuthenticated || !provider || !narrowsScopesAfterLogin(provider)) return;
    const scopeCount = countScopes(accessTokenPayload);
    // Logged before the guard below, so a session prints both halves of the story: the
    // over-scoped token a login always returns, then the narrow one that replaces it.
    // Behind the guard it could only ever print the first, which reads like a failure.
    // Worth logging at all because the outcome is invisible from outside: the token is
    // hidden from JavaScript by the service worker, the backend does not look at scopes,
    // and an over-scoped token behaves identically until it is stolen.
    if (import.meta.env.DEV) console.info(`[auth] access token carries ${scopeCount} scope(s)`);
    if (narrowingRequested.current || scopeCount <= EXPECTED_SCOPE_COUNT) return;
    narrowingRequested.current = true;
    void renewTokens().catch(() => {
      // A failed narrowing leaves a working but over-scoped token, which is worth a
      // console line and not worth interrupting someone's session over.
      console.warn('Could not narrow the token scopes; continuing with the login token.');
    });
  }, [isAuthenticated, accessTokenPayload, renewTokens, provider]);

  const reopenPlatformSignOut = useCallback(() => {
    openPlatformSignOut();
  }, []);

  // One attempt per mount. Without the ref a state change during the redirect would fire a
  // second authorize request, and the guards below are the ones that stop it firing into a
  // loop: a provider error on the URL means the last attempt was just refused, and the
  // other two are states where the person has said something that must not be overridden.
  const autoSignInFired = useRef(false);
  useEffect(() => {
    if (!autoSignIn || isAuthenticated || sessionLost || signedOut) return;
    if (hasProviderError() || autoSignInFired.current) return;
    autoSignInFired.current = true;
    void Promise.resolve(startSignIn()).catch(() => {
      // Leaves the sign-in screen on display, which already has the button this would
      // have clicked.
      autoSignInFired.current = false;
    });
  }, [autoSignIn, isAuthenticated, sessionLost, signedOut, startSignIn]);

  const account = readAccount();

  /**
   * Whether to go in rather than ask, which is the ordinary case on coming back.
   *
   * Tokens live in the service worker's memory, so an idle browser loses them while MIKE
   * Cloud's own cookie survives. The person is still signed in everywhere that matters
   * and only our copy is gone, which is not a thing worth stopping them for.
   *
   * Each condition excludes a case where asking is the right answer, and three of them
   * would otherwise be redirect loops. A remembered address is what tells us this browser
   * has been in before, so a first-ever visitor still clicks once rather than being sent
   * to an identity provider they never asked for. A deliberate sign-out must not be
   * undone, and forgetting the address is its first act, so that one is excluded twice
   * over. `sessionLost` is a 401 from our own API, which a deactivated account produces
   * on every request, so resuming it would bounce between login and 401 forever. And an
   * `error` on the URL means the provider has just refused us, so going back for another
   * refusal is the one thing certain not to help.
   */
  const canResume =
    silentResume &&
    !isAuthenticated &&
    !sessionLost &&
    !signedOut &&
    platformExit === 'none' &&
    !!account &&
    !hasProviderError();

  useEffect(() => {
    if (!canResume || resumeDeclined) return;
    // Declining is not a failure to handle: it means the one silent attempt is already
    // spent, and the sign-in screen below is what the person gets instead.
    if (!attemptSilentSignIn()) setResumeDeclined(true);
  }, [canResume, resumeDeclined]);

  // With two providers, anybody not signed in sees the email step, on whatever path they
  // arrived, and the address bar says `/signin`: that is the URL a reload and a Back both
  // understand. Replaced rather than pushed, since the page being left is not somewhere to
  // go back to unauthenticated. The per-provider screen survives only at
  // `/signin/<provider>`, where Continue lands meaning to sign in at once, where Back from
  // the identity provider returns, and where a support link points.
  //
  // It used to be the screen for a cold start with a provider remembered, on the grounds
  // that naming the account was one click instead of retyping it. The silent resume above
  // now covers that start, so what reached this screen instead was its failures: a
  // declined attempt, a refusal, an expired session. On `/signin` those got the email step
  // and on `/` the button, for no reason anybody could see.
  //
  // Not while resuming. That gate is about to leave for the identity provider, or has just
  // found a session and is leaving for the app, and a rule sending it to `/signin` in the
  // meantime undoes the redirect the moment it lands: measured as a tab sitting blank on
  // `/signin` with a `Navigate` that never took.
  const showsFrontDoor =
    providers.length > 1 &&
    providerNamedByPath(window.location.pathname) === null &&
    (!isAuthenticated || sessionLost) &&
    !(canResume && !resumeDeclined);
  useEffect(() => {
    if (showsFrontDoor && !isSignInPath(window.location.pathname)) {
      navigate(toRoutePath(signInRootPath()), { replace: true });
    }
  }, [showsFrontDoor, navigate]);

  // Rendered instead of the sign-in screen rather than after it, because the effect above
  // runs after a paint: showing "Continue as" for one frame on the way to signing in
  // silently is the flicker this whole path exists to remove. It is also the screen the
  // redirect itself shows, so nothing changes at the moment of navigation, and its own
  // delayed "Not redirected?" link is the way out if the navigation never happens.
  if (canResume && !resumeDeclined) return <RedirectingToSignIn />;

  // Before the sign-in branch on purpose: the session is still authenticated, and the one
  // thing this screen must not do is offer to sign in again.
  if (accessEnded) {
    return <AccessEndedScreen account={signedInAs ?? account} onSignOut={signOutEverywhere} />;
  }

  if (!isAuthenticated || sessionLost) {
    // Signing out wins over an expired session: a 401 on the way out would otherwise
    // relabel a deliberate sign-out as something that went wrong.
    const reason: LoginReason = signedOut ? 'signed-out' : sessionLost ? 'expired' : 'new';

    // The front door, where the address decides the provider again. Landing a sign-out on
    // the screen for the provider just left read as the sign-out not having worked, and
    // the same screen for an expired session or a refusal read the same way.
    //
    // Only where there is a front door to return to. With one provider there is no email
    // step, so the per-provider screen below is every screen.
    if (showsFrontDoor) {
      return (
        <EmailSignIn
          providers={providers}
          reason={reason}
          platformExit={platformExit}
          onReopenPlatformSignOut={reopenPlatformSignOut}
          onChoose={goToProviderScreen}
        />
      );
    }

    // `provider` is set before this component mounts and only ever null in a deployment
    // that mounts no gate at all, which cannot reach here.
    if (!provider) return null;
    return (
      <LoginScreen
        reason={reason}
        provider={provider}
        account={account}
        platformExit={platformExit}
        onSignIn={startSignIn}
        onReopenPlatformSignOut={reopenPlatformSignOut}
        onUseDifferentProvider={onUseDifferentProvider}
      />
    );
  }
  // Somebody typed an address and the session belongs to somebody else. Two different
  // events end here: an identity provider that answered from its own cookie instead of
  // honouring the address, and a session of ours that was still alive, where pressing
  // Continue never left our origin at all. Both used to go straight into the app under the
  // wrong name, which is the whole defect.
  //
  // Below the access-ended and sign-in branches on purpose: a refused session, and having
  // no session at all, are facts that outrank a question about which account was meant.
  if (
    requestedAccount &&
    signedInAs &&
    !mismatchAccepted &&
    !sameAccount(requestedAccount, signedInAs)
  ) {
    return (
      <AccountMismatchScreen
        signedInAs={signedInAs}
        requested={requestedAccount}
        onContinue={() => {
          // Answered, so it must not be asked again for the rest of this tab's life.
          forgetRequestedAccount();
          setMismatchAccepted(true);
        }}
        onSignOut={signOutEverywhere}
      />
    );
  }

  // Signed in on a sign-in path: a bookmark, a Back too far, or a callback that returned
  // here. None of them is a place to stay, so on into the app.
  if (onSignInPath) return <Navigate to="/" replace />;
  return <>{children}</>;
}
